import React, { useCallback, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { Card } from '@iris/react-components';
import clsx from 'clsx';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { Alert, Button, Glyphicon, Text } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { myWalletMonthlyPaymentFilterClick } from 'reaxl-analytics-handlers';
import { WalletSymbol } from 'reaxl-wallet';

import paymentsDuck from '@/ducks/paymentsDuck.js';

const DEFAULT_MAX_PAYMENT = 600;

const MonthlyPaymentText = React.memo(({ monthlyBudget, showPostEngagementState }) => {
    const tag = useMemo(() => ({ 'data-cmp': 'monthly-payment-text' }), []);

    const budgetValue = useMemo(() => monthlyBudget || '0', [monthlyBudget]);

    if (showPostEngagementState) {
        return (
            <div {...tag}>
                <Text
                    size={400}
                    weight="bold"
                >
                    <WalletSymbol
                        symbol="$"
                        color="default"
                        weight="bold"
                    />
                    {budgetValue}
                </Text>
                <Text
                    size={200}
                    weight="normal"
                >
                    /mo.
                </Text>
            </div>
        );
    }
    return (
        <em {...tag}>
            <Text
                size={200}
                color="gray"
                weight="normal"
            >
                ex.&nbsp;
            </Text>
            <Text
                size={300}
                color="gray"
                weight="normal"
            >
                <WalletSymbol
                    symbol="$"
                    color="gray"
                    weight="normal"
                />
                {DEFAULT_MAX_PAYMENT}
            </Text>
            <Text
                size={200}
                color="gray"
                weight="normal"
            >
                /mo.
            </Text>
        </em>
    );
});

MonthlyPaymentText.propTypes = {
    /**
     * The monthly budget set by My Wallet to display in the filter
     */
    monthlyBudget: PropTypes.number,
    /**
     * The state boolean for whether the budget has been set
     */
    showPostEngagementState: PropTypes.bool,
};

MonthlyPaymentText.displayName = 'MonthlyPaymentText';

export function MyWalletMonthlyPaymentFilterContainer() {
    const dispatch = useDispatch();

    const monthlyBudget = useSelector(
        useCallback((state) => _get(paymentsDuck.selectors.getPaymentsInfo(state), 'monthlyBudget', 0), [])
    );

    const showPaymentInFilter = useSelector(
        useCallback((state) => paymentsDuck.selectors.getMyWalletInteraction(state, 'showPaymentInFilter'), [])
    );

    const showPostEngagementState = useMemo(
        () => showPaymentInFilter && !!monthlyBudget,
        [showPaymentInFilter, monthlyBudget]
    );

    const [showBudgetUpdatedAlert, setShowBudgetUpdatedAlert] = useState(false);

    const preEngagementAnalytics = useMemo(() => ({
        cmp: 'text-link::open::set-budget',
        eventResult: 'text-link::open::set-budget',
        co_txt_url: 'Set Budget',
    }), []);

    const postEngagementAnalytics = useMemo(() => ({
        cmp: 'text-link::open::update-budget',
        eventResult: 'text-link::open::update-budget',
        co_txt_url: 'Update Budget',
    }), []);

    const updateMyWalletInteractions = useMemo(() => ({
        paymentEmphasis: true,
        disablePaymentPopover: true,
        budgetSummaryType: 'monthly',
        focusBudgetInput: true,
        showPaymentInFilter: true,
    }), []);

    const handleMyWalletFilterClick = useCallback(async (event) => {
        const analyticsData = showPostEngagementState ? postEngagementAnalytics : preEngagementAnalytics;

        sendClick(myWalletMonthlyPaymentFilterClick, event, analyticsData);

        dispatch(paymentsDuck.creators.updateMyWalletInteractions(updateMyWalletInteractions));

        dispatch(paymentsDuck.creators.updateMyWallet({
            displayModal: true,
            selectedPanelName: 'budget',
            activatedMyWalletElement: event.target,
        }));
    }, [dispatch, showPostEngagementState, preEngagementAnalytics, postEngagementAnalytics, updateMyWalletInteractions]);

    const cardClassName = useMemo(() => clsx(['display-flex flex-row align-items-center justify-content-between',
        { 'flex-row-reverse': showPostEngagementState },
    ]), [showPostEngagementState]);

    const handleAlertDismiss = useCallback(() => setShowBudgetUpdatedAlert(false), []);

    return (
        <div data-cmp="MyWalletFilter">
            {showBudgetUpdatedAlert && (
                <Alert
                    bsStyle="success"
                    withIcon={false}
                    onDismiss={handleAlertDismiss}
                >
                    The max price has been updated to match your budget.
                </Alert>
            )}
            <Card
                variant="brand-subtle"
                className={cardClassName}
            >
                <Button
                    bsStyle="text"
                    onClick={handleMyWalletFilterClick}
                >
                    {showPostEngagementState ? (
                        <>Update Budget</>
                    ) : (
                        <>
                            <Glyphicon
                                className="margin-right-1"
                                glyph="wallet"
                            />
                            Set Budget
                        </>
                    )}
                </Button>
                <MonthlyPaymentText
                    monthlyBudget={monthlyBudget}
                    showPostEngagementState={showPostEngagementState}
                />
            </Card>
        </div>
    );
}

export default React.memo(MyWalletMonthlyPaymentFilterContainer, _isEqual);
